import { useContext, useState } from "react";
import { toast } from "react-toastify";
import { createSearchParams, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import BASE_URL from "../constants/baseUrls";
import { LocaleContext } from "../routing/LangRouter";

//import utils
import { flightDataMapper } from "../utils/flightDataMapper";
import { extractErrorMessages } from "../utils/apiErrorExtractor";

const useDomesticBooking = (
    domesticFlightInformation,
    selectedFlight,
    selectedHotel,
    hotelInformation,
    selectedBus,
    busPassengers,
    uidData,
    captcha,
    captchaSlice,
    updatedFlightPassengers,
    updatedHotelPassengers,
    totalPrice,
    isPaymentByCredit,
    getCaptcha,
    setLoading,
    setNewTotalPrice,
    isTwoWay,
) => {
    //general variables
    const navigate = useNavigate();
    const accessToken = localStorage.getItem("accessToken");
    const [_, setSearchParams] = useSearchParams();
    const { t } = useTranslation();
    const { locale } = useContext(LocaleContext);
    const normalizedDepartureFlight = flightDataMapper(isTwoWay, selectedFlight, selectedFlight?.segments);
    const normalizedReturnFlight = flightDataMapper(isTwoWay, selectedFlight, selectedFlight?.return_segments);

    const [openModalPriceChange, setOpenModalPriceChange] = useState(false);
    const [responseData, setResponseData] = useState();

    const domesticBooking = async () => {
        if (!accessToken) {
            setSearchParams({ action: "login" });
            return;
        }

        //bus
        const busData = {
            uid: uidData.uid,
            source_code: selectedBus.source_code,
            bus_code: selectedBus.bus_code,
            passengers: busPassengers.passengers.map(passenger => {
                return {
                    first_name: passenger?.first_name,
                    last_name: passenger?.last_name,
                    national_code: passenger?.national_code,
                    seat_number: passenger?.seatNumber,
                    birthday: passenger?.birthdate,
                    gender: 1,
                    phone_number: passenger?.phone_number,
                };
            }),
        };

        //flight
        const flight = [];
        const pr_flight = [];
        const zm_flight = [];
        const ch118_flight = [];

        const addFlight = (flightData, isPr, isZm, isRs, isCh118) => {
            if (isPr) {
                pr_flight.push(flightData);
            }
            if (isZm) {
                zm_flight.push(flightData);
            }
            if (isRs) {
                flight.push(flightData);
            }
            if (isCh118) {
                ch118_flight.push(flightData);
            }
        };

        const createPrFlightData = flight => ({
            fare_source_code: flight?.fareSourceCode,
            adult_count: domesticFlightInformation.personCounter.adult,
            child_count: domesticFlightInformation.personCounter.child,
            infant_count: domesticFlightInformation.personCounter.infant,
            fare_type: flight?.fareType,
            passengers: updatedFlightPassengers["pr"],
        });

        const createZmFlightData = (flight, idRequest, captchaCode) => ({
            id_request: idRequest,
            captchacode: parseInt(captchaCode),
            departure_date: flight.departure_date,
            departure_time: flight.departure_time,
            flight_duration: flight.flight_duration,
            airline_code: flight?.airline_code,
            airline: flight.airline,
            airline_persian: flight.airline_persian,
            airline_logo: flight.airline_logo,
            flight_number: flight.flight_number,
            aircraft: flight.aircraft,
            departure: flight.departure,
            departure_terminal: flight.departure_terminal,
            destination: flight.destination,
            destination_terminal: flight.destination_terminal,
            cobin: flight.cobin,
            cobin_persian: flight.cobin_persian,
            type: flight.type,
            refundable: flight.refundable,
            baggage: flight.baggage,
            passengers: updatedFlightPassengers["zm"],
        });

        const createRsFlightData = flight => ({
            id: flight.id,
            capacity: flight.capacity,
            aircraft: flight.aircraft,
            cobin: flight.cobin,
            type: flight.type,
            class: flight.class,
            refundable: flight.refundable,
            adult_price: flight.adult_price,
            child_price: flight.child_price,
            infant_price: flight.infant_price,
            airline: flight.airline,
            airline_persian: flight.airline_persian,
            airline_logo: flight.airline_logo,
            flight_number: flight.flight_number,
            departure: flight.departure,
            departure_name: flight.departure_name,
            departure_date: flight.departure_date,
            departure_time: flight.departure_time,
            departure_terminal: flight.departure_terminal,
            destination: flight.destination,
            destination_name: flight.destination_name,
            destination_time: flight.destination_time,
            destination_terminal: flight.destination_terminal,
            flight_duration: flight.flight_duration,
            cobin_persian: flight.cobin_persian,
            baggage: flight.baggage,
            passengers: updatedFlightPassengers["rs"],
        });

        const createCh118FlightData = (flight, isReturn) => ({
            ref_number: flight.ref_number,
            is_return: isReturn,
            aircraft: flight.aircraft,
            destination_date: flight.arrival_date,
            flight_duration: flight.flight_duration,
            cobin: flight.cobin,
            cobin_persian: flight.cobin_persian,
            type: flight.type,
            baggage: flight?.baggage?.Quantity,
            passengers: updatedFlightPassengers["ch118"],
        });

        if (!!selectedFlight || selectedFlight?.segments?.lenght > 0) {
            if (selectedFlight.tag === "pr") {
                addFlight(createPrFlightData(normalizedDepartureFlight), true, false, false, false);
            } else if (selectedFlight.tag === "zm") {
                addFlight(createZmFlightData(selectedFlight, captcha?.departure?.id_request, captchaSlice.departure), false, true, false, false);
            } else if (selectedFlight.tag === "rs") {
                addFlight(createRsFlightData(selectedFlight), false, false, true, false);
            } else if (selectedFlight.tag === "ch118") {
                addFlight(createCh118FlightData(selectedFlight, false), false, false, false, true);
            }
        }

        if (selectedFlight?.return_segments?.lenght > 0) {
            if (selectedFlight.tag === "pr") {
                addFlight(createPrFlightData(normalizedReturnFlight), true, false, false, false);
            } else if (selectedFlight.tag === "zm") {
                addFlight(createZmFlightData(selectedFlight, captcha?.return?.id_request, captchaSlice.return), false, true, false, false);
            } else if (selectedFlight.tag === "rs") {
                addFlight(createRsFlightData(selectedFlight), false, false, true, false);
            } else if (selectedFlight.tag === "ch118") {
                addFlight(createCh118FlightData(selectedFlight, true), false, false, false, true);
            }
        }

        //hotel
        const accommodation = [];
        const pr_accommodation = [];
        const addAccommodation = (accommodationData, isGrs, isPr) => {
            if (isGrs) {
                accommodation.push(accommodationData);
            }
            if (isPr) {
                pr_accommodation.push(accommodationData);
            }
        };
        if (!!selectedHotel) {
            if (selectedHotel?.tag === "grs") {
                addAccommodation(
                    {
                        id: selectedHotel?.id,
                        check_in: hotelInformation.checkIn,
                        check_out: hotelInformation.checkOut,
                        rooms: updatedHotelPassengers,
                    },
                    true,
                    false,
                );
            } else if (selectedHotel.tag === "pr") {
                addAccommodation(updatedHotelPassengers, false, true);
            }
        }

        const data = !!selectedBus
            ? { sr360_bus: busData }
            : {
                  ...(selectedHotel && {
                      accommodation,
                      pr_accommodation,
                  }),
                  flight,
                  pr_flight,
                  zm_flight,
                  ch118_flight,
              };

        try {
            setLoading(true);
            const response = await axios.post(`${BASE_URL}/api/v2/reserve/order/`, data, {
                params: {
                    tag: selectedFlight?.tag === "pr" ? "pr" : null,
                    wallet: isPaymentByCredit,
                },
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    "Content-Type": "application/json",
                },
            });

            if (response.data.data.total_price === totalPrice) {
                if (isPaymentByCredit && !response.data.data.opg) {
                    handleDirectWalletPayment(response.data.data);
                } else {
                    window.location.replace(`${response.data.data.opg}`);
                }
            } else {
                setOpenModalPriceChange(true);
                setNewTotalPrice(response.data.data.total_price);
                setResponseData(response.data.data);
            }
        } catch (error) {
            handleError(error);
        } finally {
            setLoading(false);
        }
    };

    //handle errors during the booking process
    const handleError = error => {
        const errors = error?.response?.data?.errors;
        getCaptcha();
        if (errors && Array.isArray(errors)) {
            const { result } = extractErrorMessages(errors);
            const stayError = result.find(err => err.key === "StayNotInRange");
            if (stayError) {
                toast.error(stayError?.message, { toastId: "custom-id-StayNotInRange" });
            }
        }
        if (error?.response?.status === 401) {
            localStorage.clear();
            setSearchParams({ action: "login" });
        } else if (errors?.find(item => item.DuplicateReserveException)) {
            toast.error(t("Toast Messages.Duplicate Reservation Error"), { toastId: "custom-id-yes" });
        } else {
            toast.error(errors?.detail || t("errorMessage"), { toastId: "custom-id-yes" });
        }
    };

    // Handle direct wallet payment without gateway for zero-amount transactions
    const handleDirectWalletPayment = async paymentResult => {
        let paymentResponse;
        try {
            setLoading(true);
            const paymentData = {
                wallet: true,
                total_price: paymentResult?.total_price,
                tracking_code: paymentResult?.tracking_code,
            };
            paymentResponse = await axios.post(`${BASE_URL}/api/v2/reserve/save_payment_gateway_result/`, paymentData);
        } catch (error) {
            console.error("Payment skip process failed:", error);
        } finally {
            setLoading(false);
            navigate({
                pathname: `/${locale}/payment-result`,
                search: createSearchParams({
                    pk: paymentResponse.data.data.pk,
                    wallet: paymentResponse.data.data.wallet,
                }).toString(),
            });
        }
    };

    const ordersPayment = () => {
        if (isPaymentByCredit) {
            handleDirectWalletPayment(responseData);
        } else window.location.replace(`${responseData.opg}`);
    };

    return {
        openModalPriceChange,
        setOpenModalPriceChange,
        domesticBooking,
        ordersPayment,
    };
};

export default useDomesticBooking;
